import { useCallback, useState } from 'react';
import { useBondsAssetClassesContext } from '@/creditflowresearch/application/Bonds/BondsAssetClassesContext';
import {
  ISubscribeBySSIDPayload,
  useSubscribeBySSID,
} from '@/creditflowresearch/application/Subscriptions/useSubscribeBySSID';

interface IBondsSubscriptionStatus {
  subscriptionId: string,
  isSubscribed: boolean,
  subscriptionName: string,
}

export interface IUseBondsSubscription {
  subscriptionId: string,
  isSubscribed: boolean,
  subscribeBySSID: (payload: ISubscribeBySSIDPayload, onSuccess?: () => void) => Promise<void>,
}

/**
 * Subscribed state for a BWICs or Inventory subscribe button, for the current asset class:
 * - isSubscribedOnServer: the table's subscription status says so;
 * - isOptimisticallySubscribed: the user clicked subscribe for this asset class and the request has not
 *   failed. Set on click, cleared on failure. It covers the gap until the table data catches up, so a
 *   table refetch in between cannot flip a successful subscription back.
 */
export const useBondsSubscription = (subscriptionStatus: IBondsSubscriptionStatus): IUseBondsSubscription => {
  const { subscriptionId, isSubscribed, subscriptionName } = subscriptionStatus;
  const { currentAssetClass } = useBondsAssetClassesContext();
  const { subscribeBySSID: requestSubscription } = useSubscribeBySSID();
  const [optimisticAssetClassName, setOptimisticAssetClassName] = useState<string | null>(null);
  const currentAssetClassName = currentAssetClass?.name;

  const isSubscribedOnServer = isSubscribed && currentAssetClassName === subscriptionName;
  const isOptimisticallySubscribed = optimisticAssetClassName !== null
    && optimisticAssetClassName === currentAssetClassName;

  const subscribeBySSID = useCallback((payload: ISubscribeBySSIDPayload, onSuccess?: () => void) => {
    setOptimisticAssetClassName(currentAssetClassName ?? null);
    return requestSubscription(payload, onSuccess, () => setOptimisticAssetClassName(null));
  }, [requestSubscription, currentAssetClassName]);

  return {
    subscriptionId,
    isSubscribed: isSubscribedOnServer || isOptimisticallySubscribed,
    subscribeBySSID,
  };
};
